import { Clock, Grid2x2, Search, X } from "lucide-react";
import { useCallback, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useServiceCategories } from "@/api/hooks";
import type { RecentSearch } from "@/features/search/types";
import { cn } from "@/lib/cn";
import { getCategoryIcon } from "./category-icons";
import { isCategorySelected } from "./category-selection";
import { formatRecentSearchLabel } from "./recent-search-utils";
import { clearRecentSearches, getRecentSearches } from "./recent-searches";

interface TreatmentPanelProps {
  query: string;
  category: string | null;
  onQueryChange: (q: string) => void;
  onCategoryChange: (key: string | null, name: string | null) => void;
  onRecentSelect: (search: RecentSearch) => void;
  onSubmit: () => void;
}

let snapshot = getRecentSearches();
function subscribe(cb: () => void) {
  const handler = () => {
    snapshot = getRecentSearches();
    cb();
  };
  window.addEventListener("recent-searches-changed", handler);
  return () => window.removeEventListener("recent-searches-changed", handler);
}

// fallow-ignore-next-line complexity
function TreatmentPanel({
  query,
  category,
  onQueryChange,
  onCategoryChange,
  onRecentSelect,
  onSubmit,
}: TreatmentPanelProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [filter, setFilter] = useState(query);
  const { data } = useServiceCategories();
  const recents = useSyncExternalStore(
    subscribe,
    useCallback(() => snapshot, []),
  );

  const categories = useMemo(() => {
    const all = data?.service_categories ?? [];
    const sorted = [...all].sort((a, b) => a.display_order - b.display_order);
    if (!filter) return sorted;
    const lower = filter.toLowerCase();
    return sorted.filter((c) => c.name.toLowerCase().includes(lower));
  }, [data, filter]);

  const handleInputChange = (val: string) => {
    setFilter(val);
    onQueryChange(val);
  };

  const handleAllTreatments = () => {
    onCategoryChange(null, null);
    setFilter("");
    onQueryChange("");
  };

  const handleCategoryToggle = (key: string, name: string) => {
    onCategoryChange(key, name);
  };

  const handleClearRecents = () => {
    clearRecentSearches();
    window.dispatchEvent(new Event("recent-searches-changed"));
  };

  const showRecents = recents.length > 0 && !filter;
  const allTreatmentsActive = !category && !filter;

  return (
    <div className="max-h-[400px] overflow-y-auto">
      {/* Search input */}
      <div className="flex items-center gap-2 border-b border-fog px-4 py-3">
        <Search className="size-4 shrink-0 text-slate" />
        <input
          ref={inputRef}
          type="text"
          className="flex-1 bg-transparent text-[16px] text-ink outline-none placeholder:text-slate"
          placeholder="Search treatments or venues..."
          value={filter}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSubmit();
          }}
          // biome-ignore lint/a11y/noAutofocus: panel just opened, focusing the input is expected
          autoFocus
        />
        {filter && (
          <button
            type="button"
            className="shrink-0 rounded-xs p-0.5 text-slate transition-colors hover:text-ink"
            onClick={() => handleInputChange("")}
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>

      {/* Recent searches */}
      {showRecents && (
        <div className="border-b border-fog px-4 py-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[12px] font-semibold uppercase tracking-wider text-slate">
              Recents
            </span>
            <button
              type="button"
              className="text-[12px] font-medium text-electric-800 transition-colors hover:underline"
              onClick={handleClearRecents}
            >
              Clear
            </button>
          </div>
          <div className="flex flex-col gap-0.5">
            {recents.slice(0, 3).map((search) => (
              <button
                key={`${search.q ?? ""}-${search.category ?? ""}-${search.location ?? ""}`}
                type="button"
                className="flex items-center gap-2.5 rounded-sm px-2 py-2 text-left text-[14px] transition-colors hover:bg-fog"
                onClick={() => onRecentSelect(search)}
              >
                <Clock className="size-4 shrink-0 text-slate" />
                <span className="truncate">{formatRecentSearchLabel(search)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Treatments list */}
      <div className="px-4 py-3">
        <span className="mb-2 block text-[12px] font-semibold uppercase tracking-wider text-slate">
          Treatments
        </span>
        <div className="flex flex-col gap-0.5">
          <button
            type="button"
            className={cn(
              "flex items-center gap-3 rounded-sm px-2 py-2.5 text-left text-[14px] transition-colors",
              allTreatmentsActive
                ? "bg-electric-50 font-medium text-electric-800 hover:bg-electric-50"
                : "hover:bg-fog",
            )}
            onClick={handleAllTreatments}
          >
            <Grid2x2
              className={cn(
                "size-5 shrink-0",
                allTreatmentsActive ? "text-electric-800" : "text-slate",
              )}
              strokeWidth={1.6}
            />
            All treatments
          </button>
          {categories.map((cat) => {
            const Icon = getCategoryIcon(cat.key);
            const selected = isCategorySelected(category, cat.key);

            return (
              <button
                key={cat.id}
                type="button"
                className={cn(
                  "flex items-center gap-3 rounded-sm px-2 py-2.5 text-left text-[14px] transition-colors",
                  selected
                    ? "bg-electric-50 font-medium text-electric-800 hover:bg-electric-50"
                    : "hover:bg-fog",
                )}
                onClick={() => handleCategoryToggle(cat.key, cat.name)}
              >
                <Icon
                  className={cn("size-5 shrink-0", selected ? "text-electric-800" : "text-slate")}
                  strokeWidth={1.6}
                />
                {cat.name}
              </button>
            );
          })}
          {categories.length === 0 && filter && (
            <p className="px-2 py-4 text-center text-[13px] text-slate">No treatments found</p>
          )}
        </div>
      </div>
    </div>
  );
}

export { TreatmentPanel, type TreatmentPanelProps };
